import React, { useContext } from 'react';
import { assertNotNull } from '../../../../../../helpers/nullHelpers';
import { parseNumberOrNull } from '../../../../../../helpers/stringHelpers';
import { SelectField } from '../../../../../../shared/form/inputs/SelectField';
import { TextInputField } from '../../../../../../shared/form/inputs/TextInputField';
import { ReadOnlyField } from '../../../../../../shared/form/ReadOnlyField';
import { ManifoldFormModel } from '../../../manifolds/manifoldFormModel';
import { QuoteOrDesignFormContext } from '../../../QuoteOrDesignFormContext';
import { getThermostatSharedWithOptions, thermostatSelectOptions } from '../../../quoteOrDesignFormData';
import { roomFieldName } from '../../../quoteOrDesignFormFieldNameHelpers';
import { RoomFormModel } from '../../roomFormModel';
import { RoomAutocompleteItem } from '../../roomTypes';
import { RoomFloorConstructionField } from '../RoomFloorConstructionField';
import { RoomNameField } from '../RoomNameField';

// The Thermostat fields for a Room are:
// * Room Name
// * Floor Construction
// * Floor Covering
// * Thermostat Type
// * Thermostat Shared With
// * Number of Thermostats
// * Number of Wet Sensors
// * Number of Floor Sensors

type Props = {
  unitIndex: number;
  manifoldIndex: number;
  manifold: ManifoldFormModel;
  roomIndex: number;
  isWithinRadiatorManifold: boolean;
  room: RoomFormModel;
  handleSelectionOfRoomName: (selectedItem: RoomAutocompleteItem) => void;
};

export const RoomFieldsThermostats = ({
  unitIndex,
  manifoldIndex,
  manifold,
  roomIndex,
  isWithinRadiatorManifold,
  room,
  handleSelectionOfRoomName,
}: Props) => {
  const { referenceData } = assertNotNull(useContext(QuoteOrDesignFormContext));
  const hasSharedThermostat = room.thermostatTypeCode === 'Shared';
  const isFloorCoveringSensitive =
    room.heatLossDetails?.floorCoveringName &&
    referenceData.floorCoveringsByName[room.heatLossDetails?.floorCoveringName].isSensitive;

  const floorCoveringFieldColor =
    isFloorCoveringSensitive && parseNumberOrNull(room.numberOfFloorSensors) === 0 ? 'yellow' : 'green';

  return (
    <>
      <RoomNameField
        unitIndex={unitIndex}
        manifoldIndex={manifoldIndex}
        roomIndex={roomIndex}
        handleSelectionOfRoomName={handleSelectionOfRoomName}
      />
      <RoomFloorConstructionField
        unitIndex={unitIndex}
        manifoldIndex={manifoldIndex}
        roomIndex={roomIndex}
        isWithinRadiatorManifold={isWithinRadiatorManifold}
      />
      <ReadOnlyField
        value={room.heatLossDetails?.floorCoveringName ?? ''}
        label="Covering"
        colour={floorCoveringFieldColor}
        tooltip={
          floorCoveringFieldColor === 'yellow' ? 'Insufficient number of floor sensors for sensitive covering' : null
        }
      />
      <SelectField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'thermostatTypeCode')}
        label="Thermostat"
        options={thermostatSelectOptions}
        warning={
          hasSharedThermostat && isFloorCoveringSensitive
            ? 'You have selected a shared thermostat on a room with a sensitive floor covering'
            : undefined
        }
      />
      <SelectField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'thermostatSharedWithId')}
        label="Shared With"
        options={getThermostatSharedWithOptions(manifold)}
        disabled={!hasSharedThermostat}
      />
      {!isWithinRadiatorManifold && (
        <>
          <TextInputField
            name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfThermostats')}
            label="Thermostats"
            disabled={hasSharedThermostat}
          />
          <TextInputField
            name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfWetSensors')}
            label="Wet Sensors"
            disabled={hasSharedThermostat}
          />
          <TextInputField
            name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfFloorSensors')}
            label="Floor Sensors"
            disabled={hasSharedThermostat}
          />
        </>
      )}
    </>
  );
};
